import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { CardBrandIcon } from "./CardBrandIcons";

export type CheckoutPaymentChoice = "saved" | "one_off";

const PAYMENT_CONTROL_HEIGHT = 48;

type CheckoutPaymentMethodBoxProps = {
  bankAccount: {
    object: string;
    bankName?: string | null;
    last4?: string | null;
    accountHolderName?: string | null;
  };
  cardPaymentEnabled: boolean;
  paymentChoice: CheckoutPaymentChoice;
  onPaymentChoiceChange: (choice: CheckoutPaymentChoice) => void;
  action?: React.ReactNode;
};

const formatSavedCardLast4 = (bankAccount: CheckoutPaymentMethodBoxProps["bankAccount"]) =>
  bankAccount.last4 ? `•••• ${bankAccount.last4}` : "";

const formatSavedCardLabel = (bankAccount: CheckoutPaymentMethodBoxProps["bankAccount"]) => {
  const last4 = formatSavedCardLast4(bankAccount);
  const brand = bankAccount.bankName?.trim() || "Card";
  return last4 ? `${brand} ${last4}` : brand;
};

const formatSavedLabel = (bankAccount: CheckoutPaymentMethodBoxProps["bankAccount"]) => {
  if (bankAccount.object === "card") {
    return formatSavedCardLabel(bankAccount);
  }
  const last4 = bankAccount.last4 ? ` •••• ${bankAccount.last4}` : "";
  return `ACH Bank${last4}`;
};

const PaymentTypeDivider: React.FunctionComponent = () => (
  <Box
    aria-hidden
    sx={{
      width: "1px",
      height: 18,
      bgcolor: "grey.300",
      flexShrink: 0,
    }}
  />
);

const PaymentSectionDivider: React.FunctionComponent = () => (
  <Box
    aria-hidden
    sx={{
      width: "100%",
      height: "1px",
      bgcolor: "grey.300",
      my: 1.25,
    }}
  />
);

const PaymentTypeLabel: React.FunctionComponent<{
  icon: React.ReactNode;
  label: string;
}> = ({ icon, label }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
    {icon}
    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", letterSpacing: 0.2 }}>
      {label}
    </Typography>
  </Box>
);

const PaymentMethodTypesRow: React.FunctionComponent<{ cardPaymentEnabled: boolean }> = ({
  cardPaymentEnabled,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 1,
    }}
  >
    <PaymentTypeLabel
      icon={<AccountBalanceIcon sx={{ fontSize: 16, color: "primary.main" }} />}
      label="ACH"
    />
    {cardPaymentEnabled && (
      <>
        <PaymentTypeDivider />
        <PaymentTypeLabel
          icon={<CreditCardIcon sx={{ fontSize: 16, color: "primary.main" }} />}
          label="Debit Card"
        />
        <PaymentTypeDivider />
        <PaymentTypeLabel
          icon={<CreditCardIcon sx={{ fontSize: 16, color: "primary.main" }} />}
          label="Credit Card"
        />
      </>
    )}
  </Box>
);

export const CheckoutPaymentMethodBox: React.FunctionComponent<CheckoutPaymentMethodBoxProps> = ({
  bankAccount,
  cardPaymentEnabled,
  paymentChoice,
  onPaymentChoiceChange,
  action,
}) => {
  const savedIsCard = bankAccount.object === "card";

  const handleChange = (event: SelectChangeEvent<CheckoutPaymentChoice>) => {
    onPaymentChoiceChange(event.target.value as CheckoutPaymentChoice);
  };

  const renderSelectedValue = (value: CheckoutPaymentChoice) => {
    if (value === "one_off") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, width: "100%" }}>
          <CreditCardIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
            Pay with card
          </Typography>
        </Box>
      );
    }

    if (savedIsCard) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, width: "100%" }}>
          <CardBrandIcon brand={bankAccount.bankName} compact />
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
            {formatSavedCardLast4(bankAccount) || formatSavedCardLabel(bankAccount)}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, width: "100%" }}>
        <AccountBalanceIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
          {formatSavedLabel(bankAccount)}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minWidth: 0,
        maxWidth: 420,
      }}
    >
      <PaymentMethodTypesRow cardPaymentEnabled={cardPaymentEnabled} />
      <PaymentSectionDivider />
      <FormControl
        size="small"
        fullWidth
        sx={{
          minWidth: 0,
          "& .MuiInputBase-root": {
            minHeight: PAYMENT_CONTROL_HEIGHT,
            height: "auto",
            bgcolor: "grey.50",
          },
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            whiteSpace: "normal",
            textOverflow: "unset",
            minHeight: PAYMENT_CONTROL_HEIGHT,
            boxSizing: "border-box",
            py: 1.25,
          },
        }}
      >
        <InputLabel id="checkout-payment-method-label">Payment Method</InputLabel>
        <Select
          labelId="checkout-payment-method-label"
          id="checkout-payment-method"
          value={paymentChoice}
          label="Payment Method"
          onChange={handleChange}
          renderValue={renderSelectedValue}
          MenuProps={{
            PaperProps: {
              sx: { minWidth: 280, maxWidth: 420 },
            },
          }}
        >
            <MenuItem value="saved">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
                {savedIsCard ? (
                  <CardBrandIcon brand={bankAccount.bankName} compact />
                ) : (
                  <AccountBalanceIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                )}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {savedIsCard ? "Use saved card" : "Use saved bank account (ACH)"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatSavedLabel(bankAccount)}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
            {cardPaymentEnabled && (
              <MenuItem value="one_off">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
                  <CreditCardIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Pay with card
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Enter card when you confirm your order
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            )}
          </Select>
        </FormControl>
      {action}
    </Box>
  );
};

export default CheckoutPaymentMethodBox;
